import React, { useState, useEffect, useRef } from "react";
import "./Login.css";
import image from "../../Assets/Images/Login/newlogin.svg";
import { FaUserShield } from "react-icons/fa";
import { RiLockPasswordFill } from "react-icons/ri";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { useDispatch } from "react-redux";
import { fetchApiData } from "../../Redux/features/Landing/landingSlice";
import { UAParser } from "ua-parser-js";
import EndPoints from "../../Api/Endpoints";
import { GET, POST, PUT } from "../../Api/HttpsMethods";

const Login = ({ setAuthenticated }) => {
  // -------------------- Device / Browser Info --------------------
  const [userInfo, setUserInfo] = useState({
    ipAddress: null,
    location: null,
    browser: null,
    operatingSystem: null,
    deviceType: null,
    screenResolution: null,
    isLoading: true,
    error: null,
  });

  const detectDeviceType = () => {
    const ua = navigator.userAgent.toLowerCase();
    const isMobile =
      /android|webos|iphone|ipod|blackberry|iemobile|opera mini|mobile/i.test(
        ua
      );
    const isTablet =
      /ipad|tablet|kindle/i.test(ua) ||
      (window.screen?.width >= 768 &&
        window.screen?.width <= 1024 &&
        /android/i.test(ua));
    if (isTablet) return "Tablet";
    if (isMobile) return "Mobile";
    return "Desktop";
  };

  const detectBrowser = () => {
    const parser = new UAParser(navigator.userAgent);
    const res = parser.getResult();
    const browserName = res.browser?.name || "Unknown";
    const browserVer = res.browser?.version || "";
    const osName = res.os?.name || "Unknown";
    const osVer = res.os?.version || "";
    return {
      browser: [browserName, browserVer].filter(Boolean).join(" "),
      os: [osName, osVer].filter(Boolean).join(" "),
      device: res.device?.type || "Desktop",
    };
  };

  const fetchIPInfo = async () => {
    const apis = [
      {
        url: "https://ipapi.co/json/",
        handler: (d) => ({
          ip: d.ip,
          location: `${d.city}, ${d.region}, ${d.country_name}`,
        }),
      },
      {
        url: "https://ip-api.com/json/",
        handler: (d) => ({
          ip: d.query,
          location: `${d.city}, ${d.regionName}, ${d.country}`,
        }),
      },
      {
        url: "https://ipinfo.io/json",
        handler: (d) => ({
          ip: d.ip,
          location: `${d.city}, ${d.region}, ${d.country}`,
        }),
      },
    ];
    for (const api of apis) {
      try {
        const r = await fetch(api.url);
        if (r.ok) {
          const data = await r.json();
          return api.handler(data);
        }
      } catch (_) {
        /* try next */
      }
    }
    return { ip: null, location: null };
  };

  useEffect(() => {
    (async () => {
      try {
        const [{ ip, location }, ua] = await Promise.all([
          fetchIPInfo(),
          Promise.resolve(detectBrowser()),
        ]);
        const device = detectDeviceType();

        setUserInfo({
          ipAddress: ip,
          location,
          browser: ua.browser,
          operatingSystem: ua.os,
          deviceType: ua.device !== "Desktop" ? ua.device : device,
          screenResolution: {
            width: window.screen?.width,
            height: window.screen?.height,
            devicePixelRatio: window.devicePixelRatio || 1,
          },
          isLoading: false,
          error: null,
        });
      } catch (err) {
        setUserInfo((p) => ({ ...p, isLoading: false, error: String(err) }));
      }
    })();
  }, []);

  // -------------------- Login & Reset States --------------------
  const dispatch = useDispatch();
  const passwordRef = useRef(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [inputState, setInputState] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messageType, setMessageType] = useState("error");
  // Force Password Reset mode
  const [needsPasswordReset, setNeedsPasswordReset] = useState(false);
  const [resetNewPassword, setResetNewPassword] = useState("");
  const [resetConfirmPassword, setResetConfirmPassword] = useState("");
  const [resetContext, setResetContext] = useState({
    user_id: null,
    client_id: null,
    modified_by: "",
  });
  const [isResetting, setIsResetting] = useState(false);

  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  // -------------------- Handlers --------------------
  const handleRememberMeChange = (e) => setRememberMe(e.target.checked);

  const handleForgotPasswordClick = (e) => {
    e.preventDefault();
    setErrorMessage(
      "Please contact the System Administrator to reset your password."
    );
    setInputState(false);
  };

  const handleOAuthLogin = async () => {
    const oauthUsername = "siddharth";
    const oauthPassword = "siddharth123";
    try {
      const response = await fetch(
        `${EndPoints.PRISM_TOKEN}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            accept: "application/json",
          },
          body: new URLSearchParams({
            grant_type: "",
            username: oauthUsername,
            password: oauthPassword,
            scope: "",
            client_id: "",
            client_secret: "",
          }),
        }
      );
      const data = await response.json();

      if (response.ok && data?.access_token) {
        localStorage.setItem("other", data.access_token);
        if (data.token_type)
          localStorage.setItem("other_type", data.token_type);
        if (data.expires_in)
          localStorage.setItem("other_expires_in", String(data.expires_in));
        return data.access_token;
      } else {
        setErrorMessage("OAuth2 authentication failed.");
        return null;
      }
    } catch (error) {
      setErrorMessage("An error occurred during OAuth2 authentication.");
      return null;
    }
  };

  const fetchAppToken = async () => {
    try {
      const response = await fetch(
        "https://prism-dev.dse-cc-api.com/prism/api/v1/app-token",
        // "http://127.0.0.1:8000/prism/api/v1/app-token",
        {
          method: "POST",
          headers: {
            accept: "application/json",
          },
          body: "",
        }
      );

      const data = await response.json();

      if (response.ok && data?.access_token) {
        localStorage.setItem("other", data.access_token);
        if (data.token_type)
          localStorage.setItem("other_type", data.token_type);
        return data.access_token;
      } else {
        console.error("Failed to fetch app token");
        return null;
      }
    } catch (error) {
      console.error("Error fetching app token:", error);
      return null;
    }
  };

   const handleLogin = async(e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if(!username || !password) {
        setErrorMessage("fields are empty");
        setInputState(true);
        return;
      }
      
      // const url = `${EndPoints.LoginUser}?user_name=${username}&password=${password}`
      
      // const payload = {
      //   "device-type": userInfo?.deviceType ?? "",
      // "ip-address": userInfo?.ipAddress ?? "",
      // "browser-name": userInfo?.browser ?? "",
      // };

      // const data = await POST.noToken(url, payload);

      const url = `${EndPoints.LoginUser}`;

      const payload = {
        user_name: username,
        password: password,
      };

      const headers = {
        "device-type": userInfo?.deviceType ?? "",
        "ip-address": userInfo?.ipAddress ?? "",
        "browser-name": userInfo?.browser ?? "",
      };

      const res = await fetch(url, {
        method: "POST",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
          ...headers,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      // console.log("Login Response:", data);
      
      if(!data) {
        setErrorMessage("No response received from server");
        return;
      }
     
      // if(data?.status === 403 && Number(data?.reset_password) === 1) {
      if(Number(data?.reset_password) === 1) {
        setNeedsPasswordReset(true);
        setResetContext({
          user_id:data?.user_id ?? null,
          client_id: data?.client_id ?? null,
        modified_by: username,
        });

        setResetNewPassword("");
      setResetConfirmPassword("");
      setErrorMessage(
        data?.message || "Your password must be reset before you can log in."
      );
      setInputState(false);
      return; 
    }

    if(data?.status === 200 && /login/i.test(data?.message || "")) {
      await fetchAppToken();
      localStorage.setItem("isLoggedIn", true);

      if (data.user_id) localStorage.setItem("userId", String(data.user_id));
      if (data.client_id) localStorage.setItem("clientId", String(data.client_id));
      if (data.client_branch_id)
        localStorage.setItem("clientBranchId", String(data.client_branch_id));


      dispatch(
        fetchApiData({
          userId: data.user_id,
          clientId: data.client_id,
          clientBranchId: data.client_branch_id,
        })
      );

      setAuthenticated(true);
      setInputState(false);
      setErrorMessage("");
      return;
    }

    setErrorMessage(
      data?.message || "Login Failed. Please check your username and password."
    );
    setInputState(true);
    setAuthenticated(false);
  } catch (error) {
    console.error("Login Error:", error);
    setErrorMessage("An error occurred during login. Please try again.");
    setAuthenticated(false);
  } finally {
    setIsSubmitting(false);
      }
    };
   
  const validateNewPassword = (password) => {
    if (!password) return "Password cannot be empty";
    if (password.length < 6)
      return "Password must be at least 6 characters long";
    if (!/[A-Z]/.test(password))
      return "Password must contain at least 1 uppercase letter";
    if (!/[a-z]/.test(password))
      return "Password must contain at least 1 lowercase letter";
    if (!/[0-9]/.test(password))
      return "Password must contain at least 1 number";
    if (!/[!@#$%^&*]/.test(password))
      return "Password must contain at least 1 special character (!@#$%^&*)";
    return null; // valid
  };

  // const handleResetPassword = async (e) => {
  //   e.preventDefault();

  //   const passwordError = validateNewPassword(resetNewPassword);
  //   if (passwordError) {
  //     setErrorMessage(passwordError);
  //     return;
  //   }

  //   if (resetNewPassword !== resetConfirmPassword) {
  //     setErrorMessage("Passwords do not match.");
  //     return;
  //   }

  //   setIsResetting(true);
  //   setErrorMessage("");

  //   try {
  //     const { user_id, client_id, modified_by } = resetContext;

  //     const url = `${EndPoints.PRISM_RESET_NEW_PASSWORD}?user_id=${encodeURIComponent(
  //       user_id
  //     )}&client_id=${encodeURIComponent(
  //       client_id
  //     )}&new_password=${encodeURIComponent(
  //       resetNewPassword
  //     )}&modified_by=${encodeURIComponent(modified_by || username)}`;

  //     const res = await fetch(url, {
  //       method: "PUT",
  //       headers: { accept: "application/json" },
  //     });

  //     const data = await res.json();

  //     if (data?.status === 200) {
  //       // Back to normal login mode
  //       setNeedsPasswordReset(false);
  //       // setPassword(resetNewPassword); // optionally prefill
  //       setUsername("");
  //       setPassword("");
  //       setResetNewPassword("");
  //       setResetConfirmPassword("");
  //       setMessageType("success");
  //       setErrorMessage("Password reset successful. Please log in.");
  //       // You can auto-login here if you want by calling handleLogin(...)
  //     } else {
  //       setErrorMessage(data?.message || "Password reset failed.");
  //     }
  //   } catch (err) {
  //     console.error("Reset Password Error:", err);
  //     setErrorMessage("An error occurred while resetting the password.");
  //   } finally {
  //     setIsResetting(false);
  //   }
  // };

  // -------------------- Render --------------------
 
 
  const handleResetPassword = async (e) => {
    e.preventDefault();

    const passwordError = validateNewPassword(resetNewPassword);
    if (passwordError) {
      setErrorMessage(passwordError);
      return;
    }

    if (resetNewPassword !== resetConfirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    setIsResetting(true);
    setErrorMessage("");

    try {
      // ✅ Step 1: Get app token first (fixes "Not authenticated" error)
      const appToken = await fetchAppToken();
      if (!appToken) {
        setErrorMessage("Authentication failed. Please try again.");
        return;
      }

      const { user_id, client_id, modified_by } = resetContext;

      // const url = `${EndPoints.PRISM_RESET_NEW_PASSWORD}?user_id=${encodeURIComponent(
      //   user_id
      // )}&client_id=${encodeURIComponent(
      //   client_id
      // )}&new_password=${encodeURIComponent(
      //   resetNewPassword
      // )}&modified_by=${encodeURIComponent(modified_by || username)}`;

      // // ✅ Step 2: Pass the token in Authorization header
      // const res = await fetch(url, {
      //   method: "PUT",
      //   headers: {
      //     accept: "application/json",
      //     Authorization: `Bearer ${appToken}`,  // ← this was missing
      //   },
      // });

      const res = await fetch(`${EndPoints.PRISM_RESET_NEW_PASSWORD}`, {
        method: "PUT",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${appToken}`,
        },
        body: JSON.stringify({
          user_id,
          client_id,
          new_password: resetNewPassword,
          modified_by: modified_by || username,
        }),
      });

      const data = await res.json();

      if (data?.status === 200) {
        setNeedsPasswordReset(false);
        setUsername("");
        setPassword("");
        setResetNewPassword("");
        setResetConfirmPassword("");
        setMessageType("success");
        setErrorMessage("Password reset successful. Please log in.");
      } else {
        setErrorMessage(data?.message || "Password reset failed.");
      }
    } catch (err) {
      console.error("Reset Password Error:", err);
      setErrorMessage("An error occurred while resetting the password.");
    } finally {
      setIsResetting(false);
    }
  };


  return (
    <div className="loginPage lg-flex" style={{ flexDirection: "column" }}>
      <div className="lg-container lg-flex">
        <div className="videoDiv">
          <img src={image} alt="education" />
          <div className="textDiv">
            <h2 className="lg-title">
              <span style={{ fontWeight: "200" }}> </span>
              <span className="lead-word"></span>
            </h2>
          </div>
        </div>

        <div className="formDiv lg-flex">
          <div className="lg-error">
            {errorMessage && (
              <div
                style={{
                  color: messageType === "error" ? "red" : "green",
                  marginTop: "10px",
                }}
              >
                {errorMessage}
              </div>
            )}
          </div>

          <form action="" className="form">
            <div className="lg-input-containers-res grid">
              {/* Username */}
              <div className="inputDiv">
                <label htmlFor="username">Username</label>
                <div
                  className="lg-input lg-flex"
                  onClick={(e) => {
                    // Prevent focusing when clicking icons (eye icon etc.)
                    if (e.target.tagName !== "INPUT") {
                      const inputEl = e.currentTarget.querySelector("input");
                      inputEl?.focus();
                    }
                  }}
                  style={
                    inputState && !needsPasswordReset
                      ? { border: "1px solid red" }
                      : {}
                  }
                >
                  <FaUserShield className="iconav" style={{ color: "black" }} />
                  <input
                    type="text"
                    id="usernames"
                    placeholder="Enter Username"
                    value={username}
                    // onChange={(e) => {
                    //   setUsername(e.target.value);
                    //   if (inputState) setInputState(false);
                    // }}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const sanitized = raw
                        .toLowerCase()
                        .replace(/[^a-z0-9_]/g, "");
                      setUsername(sanitized);
                      if (inputState) setInputState(false);
                    }}
                    readOnly={needsPasswordReset}
                  />
                </div>
              </div>

              {/* Normal Login: Password */}
              {!needsPasswordReset && (
                // <div className="inputDiv">
                //   <label htmlFor="password">Password</label>
                //   <div
                //     className="lg-input lg-flex"
                //     style={inputState ? { border: "1px solid red" } : {}}
                //   >
                //     <RiLockPasswordFill className="iconav" style={{ color: "black" }} />
                //     <input
                //       type="password"
                //       id="password"
                //       placeholder="Enter Password"
                //       value={password}
                //       onChange={(e) => {
                //         setPassword(e.target.value);
                //         if (inputState) setInputState(false);
                //       }}
                //       className="hide-reveal"
                //     />
                //   </div>
                // </div>

                <div className="inputDiv">
                  <label htmlFor="password">Password</label>
                  <div
                    className="lg-input lg-flex"
                    style={inputState ? { border: "1px solid red" } : {}}
                    onClick={(e) => {
                      // Prevent focusing when clicking icons (eye icon etc.)
                      if (e.target.tagName !== "INPUT") {
                        const inputEl = e.currentTarget.querySelector("input");
                        inputEl?.focus();
                      }
                    }}
                  >
                    <RiLockPasswordFill
                      className="iconav"
                      style={{ color: "black" }}
                    />
                    <input
                      ref={passwordRef}
                      type={showPassword ? "text" : "password"} // toggle here
                      id="password"
                      placeholder="Enter Password"
                      value={password}
                      autoComplete="new-password"
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (inputState) setInputState(false);
                      }}
                      className="hide-reveal"
                    />
                    <span
                      className="eye-icon"
                      onClick={togglePasswordVisibility}
                      style={{ cursor: "pointer", marginLeft: "25px" }}
                    >
                      {showPassword ? (
                        <AiOutlineEyeInvisible />
                      ) : (
                        <AiOutlineEye />
                      )}
                    </span>
                  </div>
                </div>
              )}

              {/* Reset Mode: New Password + Confirm Password */}
              {needsPasswordReset && (
                <>
                  <div className="inputDiv">
                    <label htmlFor="new_password">New Password</label>
                    <div className="lg-input lg-flex">
                      <RiLockPasswordFill
                        className="iconav"
                        style={{ color: "black" }}
                      />
                      <input
                        type="password"
                        id="new_password"
                        placeholder="Enter New Password"
                        value={resetNewPassword}
                        onChange={(e) => setResetNewPassword(e.target.value)}
                        style={{ width: "250px" }}
                        className="hide-reveal"
                      />
                    </div>
                  </div>

                  {/* <div className="inputDiv">
                    <label htmlFor="confirm_password">Confirm Password</label>
                    <div className="lg-input lg-flex">
                      <RiLockPasswordFill className="iconav" style={{ color: "black" }} />
                      <input
                        type="password"
                        id="confirm_password"
                        placeholder="Confirm New Password"
                        value={resetConfirmPassword}
                        onChange={(e) => setResetConfirmPassword(e.target.value)}
                        className="hide-reveal" 
                      />
                    </div>
                  </div> */}
                  <div className="inputDiv">
                    <label htmlFor="confirm_password">Confirm Password</label>
                    <div
                      className="lg-input lg-flex"
                      style={inputState ? { border: "1px solid red" } : {}}
                    >
                      <RiLockPasswordFill
                        className="iconav"
                        style={{ color: "black" }}
                      />
                      <input
                        type={showPassword ? "text" : "password"} // toggle here
                        id="confirm_password"
                        placeholder="Confirm New Password"
                        value={resetConfirmPassword}
                        onChange={(e) =>
                          setResetConfirmPassword(e.target.value)
                        }
                        className="hide-reveal"
                      />
                      <span
                        className="eye-icon"
                        onClick={togglePasswordVisibility}
                        style={{ cursor: "pointer", marginLeft: "45px" }}
                      >
                        {showPassword ? (
                          <AiOutlineEyeInvisible />
                        ) : (
                          <AiOutlineEye />
                        )}
                      </span>
                    </div>
                  </div>
                </>
              )}
              {/* Remember + Forgot (hidden in reset mode) */}
              {!needsPasswordReset && (
                <div className="forgotPassword">
                  <input
                    type="checkbox"
                    id="rememberMe"
                    name="rememberMe"
                    checked={rememberMe}
                    onChange={handleRememberMeChange}
                  />
                  <label
                    className="logged-in"
                    htmlFor="rememberMe"
                    style={{ color: "rgb(66, 66, 66)", marginLeft: "5px" }}
                  >
                    Keep Me Logged In
                  </label>
                  <a
                    onClick={handleForgotPasswordClick}
                    style={{
                      marginLeft: "30px",
                      color: "#4933ff",
                      fontWeight: "600",
                    }}
                    className="password-forget"
                  >
                    Forgot Password?
                  </a>
                </div>
              )}
            </div>

            <button
              type="submit"
              className="lg-btn lg-flex"
              // onClick={handleLogin}
              onClick={needsPasswordReset ? handleResetPassword : handleLogin}
              style={{
                marginTop: "50px",
                opacity: isSubmitting || isResetting ? 0.7 : 1,
                pointerEvents: isSubmitting || isResetting ? "none" : "auto",
              }}
              disabled={isSubmitting || isResetting}
            >
              {needsPasswordReset
                ? isResetting
                  ? "Resetting..."
                  : "Submit"
                : isSubmitting
                ? "Logging in..."
                : "Login"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;





















// import React, { useState, useEffect, useRef } from "react";
// import "./Login.css";
// import image from "../../Assets/Images/Login/newlogin.svg";
// import { FaUserShield } from "react-icons/fa";
// import { RiLockPasswordFill } from "react-icons/ri";
// import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
// import { useDispatch } from "react-redux";
// import { fetchApiData } from "../../Redux/features/Landing/landingSlice";
// import { UAParser } from "ua-parser-js";
// import EndPoints from "../../Api/Endpoints";
// import { GET, POST, PUT } from "../../Api/HttpsMethods";

// const Login = ({ setAuthenticated }) => {
//   const [userInfo, setUserInfo] = useState({
//     ipAddress: null,
//     location: null,
//     browser: null,
//     operatingSystem: null,
//     deviceType: null,
//     screenResolution: null,
//     isLoading: true,
//     error: null,
//   });

//   const detectDeviceType = () => {
//     const ua = navigator.userAgent.toLowerCase();
//     const isMobile =
//       /android|webos|iphone|ipod|blackberry|iemobile|opera mini|mobile/i.test(ua);
//     const isTablet =
//       /ipad|tablet|kindle/i.test(ua) ||
//       (window.screen?.width >= 768 &&
//         window.screen?.width <= 1024 &&
//         /android/i.test(ua));
//     if (isTablet) return "Tablet";
//     if (isMobile) return "Mobile";
//     return "Desktop";
//   };

//   const detectBrowser = () => {
//     const parser = new UAParser(navigator.userAgent);
//     const res = parser.getResult();
//     const browserName = res.browser?.name || "Unknown";
//     const browserVer = res.browser?.version || "";
//     const osName = res.os?.name || "Unknown";
//     const osVer = res.os?.version || "";
//     return {
//       browser: [browserName, browserVer].filter(Boolean).join(" "),
//       os: [osName, osVer].filter(Boolean).join(" "),
//       device: res.device?.type || "Desktop",
//     };
//   };

//   const fetchIPInfo = async () => {
//     const apis = [
//       {
//         url: "https://ipapi.co/json/",
//         handler: (d) => ({
//           ip: d.ip,
//           location: `${d.city}, ${d.region}, ${d.country_name}`,
//         }),
//       },
//       {
//         url: "https://ip-api.com/json/",
//         handler: (d) => ({
//           ip: d.query,
//           location: `${d.city}, ${d.regionName}, ${d.country}`,
//         }),
//       },
//       {
//         url: "https://ipinfo.io/json",
//         handler: (d) => ({
//           ip: d.ip,
//           location: `${d.city}, ${d.region}, ${d.country}`,
//         }),
//       },
//     ];
//     for (const api of apis) {
//       try {
//         const r = await fetch(api.url);
//         if (r.ok) {
//           const data = await r.json();
//           return api.handler(data);
//         }
//       } catch (_) { /* try next */ }
//     }
//     return { ip: null, location: null };
//   };

//   useEffect(() => {
//     (async () => {
//       try {
//         const [{ ip, location }, ua] = await Promise.all([
//           fetchIPInfo(),
//           Promise.resolve(detectBrowser()),
//         ]);
//         const device = detectDeviceType();
//         setUserInfo({
//           ipAddress: ip,
//           location,
//           browser: ua.browser,
//           operatingSystem: ua.os,
//           deviceType: ua.device !== "Desktop" ? ua.device : device,
//           screenResolution: {
//             width: window.screen?.width,
//             height: window.screen?.height,
//             devicePixelRatio: window.devicePixelRatio || 1,
//           },
//           isLoading: false,
//           error: null,
//         });
//       } catch (err) {
//         setUserInfo((p) => ({ ...p, isLoading: false, error: String(err) }));
//       }
//     })();
//   }, []);

//   // -------------------- States --------------------
//   const dispatch = useDispatch();
//   const passwordRef = useRef(null);
//   const [username, setUsername] = useState("");
//   const [password, setPassword] = useState("");
//   const [rememberMe, setRememberMe] = useState(false);
//   const [inputState, setInputState] = useState(false);
//   const [errorMessage, setErrorMessage] = useState("");
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [messageType, setMessageType] = useState("error");
//   const [needsPasswordReset, setNeedsPasswordReset] = useState(false);
//   const [resetNewPassword, setResetNewPassword] = useState("");
//   const [resetConfirmPassword, setResetConfirmPassword] = useState("");
//   const [resetContext, setResetContext] = useState({
//     user_id: null,
//     client_id: null,
//     modified_by: "",
//   });
//   const [isResetting, setIsResetting] = useState(false);
//   const [showPassword, setShowPassword] = useState(false);

//   const togglePasswordVisibility = () => setShowPassword((prev) => !prev);
//   const handleRememberMeChange = (e) => setRememberMe(e.target.checked);

//   const handleForgotPasswordClick = (e) => {
//     e.preventDefault();
//     setErrorMessage("Please contact the System Administrator to reset your password.");
//     setInputState(false);
//   };

//   // -------------------- Step 1: Fetch App Token --------------------
//   const fetchAppToken = async () => {
//     try {
//       const response = await fetch(EndPoints.PRISM_APP_TOKEN, {
//         method: "POST",
//         headers: {
//           accept: "application/json",
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify({
//           access_token: EndPoints.PRISM_STATIC_TOKEN,
//           token_type: "bearer",
//         }),
//       });

//       const data = await response.json();

//       if (response.ok && data?.access_token) {
//         localStorage.setItem("other", data.access_token);
//         if (data.token_type) localStorage.setItem("other_type", data.token_type);
//         return data.access_token;
//       } else {
//         console.error("Failed to fetch app token:", data);
//         return null;
//       }
//     } catch (error) {
//       console.error("Error fetching app token:", error);
//       return null;
//     }
//   };

//   // -------------------- Step 2: Login --------------------
//   const handleLogin = async (e) => {
//     e.preventDefault();
//     setIsSubmitting(true);

//     try {
//       if (!username || !password) {
//         setErrorMessage("Fields are empty");
//         setInputState(true);
//         return;
//       }

//       const url = `${EndPoints.LoginUser}?user_name=${username}&password=${password}`;

//       const payload = {
//         "device-type": userInfo?.deviceType ?? "",
//         "ip-address": userInfo?.ipAddress ?? "",
//         "browser-name": userInfo?.browser ?? "",
//       };

//       const data = await POST.noToken(url, payload);
//       console.log("Login Response:", data);

//       if (!data) {
//         setErrorMessage("No response received from server");
//         return;
//       }

//       // ✅ Force password reset check
//       if (Number(data?.reset_password) === 1) {
//         setNeedsPasswordReset(true);
//         setResetContext({
//           user_id: data?.user_id ?? null,
//           client_id: data?.client_id ?? null,
//           modified_by: username,
//         });
//         setResetNewPassword("");
//         setResetConfirmPassword("");
//         setMessageType("error");
//         setErrorMessage(data?.message || "Your password must be reset before you can log in.");
//         setInputState(false);
//         return;
//       }

//       // ✅ Successful login
//       if (data?.status === 200 && /login/i.test(data?.message || "")) {
//         // Fetch app token AFTER successful login
//         await fetchAppToken();

//         localStorage.setItem("isLoggedIn", "true");
//         if (data.user_id != null) localStorage.setItem("userId", String(data.user_id));
//         if (data.client_id != null) localStorage.setItem("clientId", String(data.client_id));
//         if (data.client_branch_id != null)
//           localStorage.setItem("clientBranchId", String(data.client_branch_id));

//         dispatch(
//           fetchApiData({
//             userId: data.user_id ?? null,
//             clientId: data.client_id ?? null,
//             clientBranchId: data.client_branch_id ?? null,
//           })
//         );

//         setAuthenticated(true);
//         setInputState(false);
//         setErrorMessage("");
//         return;
//       }

//       setErrorMessage(data?.message || "Login Failed. Please check your username and password.");
//       setInputState(true);
//       setAuthenticated(false);
//     } catch (error) {
//       console.error("Login Error:", error);
//       setErrorMessage("An error occurred during login. Please try again.");
//       setAuthenticated(false);
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   // -------------------- Password Validation --------------------
//   const validateNewPassword = (pwd) => {
//     if (!pwd) return "Password cannot be empty";
//     if (pwd.length < 6) return "Password must be at least 6 characters long";
//     if (!/[A-Z]/.test(pwd)) return "Password must contain at least 1 uppercase letter";
//     if (!/[a-z]/.test(pwd)) return "Password must contain at least 1 lowercase letter";
//     if (!/[0-9]/.test(pwd)) return "Password must contain at least 1 number";
//     if (!/[!@#$%^&*]/.test(pwd)) return "Password must contain at least 1 special character (!@#$%^&*)";
//     return null;
//   };

//   // -------------------- Step 3: Reset Password --------------------
//   const handleResetPassword = async (e) => {
//     e.preventDefault();

//     const passwordError = validateNewPassword(resetNewPassword);
//     if (passwordError) {
//       setErrorMessage(passwordError);
//       return;
//     }

//     if (resetNewPassword !== resetConfirmPassword) {
//       setErrorMessage("Passwords do not match.");
//       return;
//     }

//     setIsResetting(true);
//     setErrorMessage("");

//     try {
//       const { user_id, client_id, modified_by } = resetContext;

//       // ✅ Step 1: Always get a fresh app token before reset
//       const appToken = await fetchAppToken();
//       if (!appToken) {
//         setErrorMessage("Authentication failed. Please try again.");
//         return;
//       }

//       // ✅ Step 2: Build URL with query params
//       const url = `${EndPoints.PRISM_RESET_NEW_PASSWORD}?user_id=${encodeURIComponent(
//         user_id
//       )}&client_id=${encodeURIComponent(
//         client_id
//       )}&new_password=${encodeURIComponent(
//         resetNewPassword
//       )}&modified_by=${encodeURIComponent(modified_by || username)}`;

//       // ✅ Step 3: PUT request with Bearer token in Authorization header
//       const res = await fetch(url, {
//         method: "PUT",
//         headers: {
//           accept: "application/json",
//           Authorization: `Bearer ${appToken}`, // ← required for reset
//         },
//       });

//       const data = await res.json();
//       console.log("Reset Password Response:", data);

//       if (data?.status === 200) {
//         // ✅ Reset success — go back to login
//         setNeedsPasswordReset(false);
//         setUsername("");
//         setPassword("");
//         setResetNewPassword("");
//         setResetConfirmPassword("");
//         setMessageType("success");
//         setErrorMessage("Password reset successful. Please log in.");
//       } else {
//         setMessageType("error");
//         setErrorMessage(data?.message || "Password reset failed. Please try again.");
//       }
//     } catch (err) {
//       console.error("Reset Password Error:", err);
//       setMessageType("error");
//       setErrorMessage("An error occurred while resetting the password.");
//     } finally {
//       setIsResetting(false);
//     }
//   };

//   // -------------------- Render --------------------
//   return (
//     <div className="loginPage lg-flex" style={{ flexDirection: "column" }}>
//       <div className="lg-container lg-flex">
//         <div className="videoDiv">
//           <img src={image} alt="education" />
//           <div className="textDiv">
//             <h2 className="lg-title">
//               <span style={{ fontWeight: "200" }}> </span>
//               <span className="lead-word"></span>
//             </h2>
//           </div>
//         </div>

//         <div className="formDiv lg-flex">
//           <div className="lg-error">
//             {errorMessage && (
//               <div
//                 style={{
//                   color: messageType === "error" ? "red" : "green",
//                   marginTop: "10px",
//                 }}
//               >
//                 {errorMessage}
//               </div>
//             )}
//           </div>

//           <form action="" className="form">
//             <div className="lg-input-containers-res grid">

//               {/* Username */}
//               <div className="inputDiv">
//                 <label htmlFor="username">Username</label>
//                 <div
//                   className="lg-input lg-flex"
//                   onClick={(e) => {
//                     if (e.target.tagName !== "INPUT") {
//                       e.currentTarget.querySelector("input")?.focus();
//                     }
//                   }}
//                   style={inputState && !needsPasswordReset ? { border: "1px solid red" } : {}}
//                 >
//                   <FaUserShield className="iconav" style={{ color: "black" }} />
//                   <input
//                     type="text"
//                     id="usernames"
//                     placeholder="Enter Username"
//                     value={username}
//                     onChange={(e) => {
//                       const sanitized = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "");
//                       setUsername(sanitized);
//                       if (inputState) setInputState(false);
//                     }}
//                     readOnly={needsPasswordReset}
//                   />
//                 </div>
//               </div>

//               {/* Normal Login: Password */}
//               {!needsPasswordReset && (
//                 <div className="inputDiv">
//                   <label htmlFor="password">Password</label>
//                   <div
//                     className="lg-input lg-flex"
//                     style={inputState ? { border: "1px solid red" } : {}}
//                     onClick={(e) => {
//                       if (e.target.tagName !== "INPUT") {
//                         e.currentTarget.querySelector("input")?.focus();
//                       }
//                     }}
//                   >
//                     <RiLockPasswordFill className="iconav" style={{ color: "black" }} />
//                     <input
//                       ref={passwordRef}
//                       type={showPassword ? "text" : "password"}
//                       id="password"
//                       placeholder="Enter Password"
//                       value={password}
//                       autoComplete="new-password"
//                       onChange={(e) => {
//                         setPassword(e.target.value);
//                         if (inputState) setInputState(false);
//                       }}
//                       className="hide-reveal"
//                     />
//                     <span
//                       className="eye-icon"
//                       onClick={togglePasswordVisibility}
//                       style={{ cursor: "pointer", marginLeft: "25px" }}
//                     >
//                       {showPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
//                     </span>
//                   </div>
//                 </div>
//               )}

//               {/* Reset Mode: New + Confirm Password */}
//               {needsPasswordReset && (
//                 <>
//                   <div className="inputDiv">
//                     <label htmlFor="new_password">New Password</label>
//                     <div className="lg-input lg-flex">
//                       <RiLockPasswordFill className="iconav" style={{ color: "black" }} />
//                       <input
//                         type="password"
//                         id="new_password"
//                         placeholder="Enter New Password"
//                         value={resetNewPassword}
//                         onChange={(e) => setResetNewPassword(e.target.value)}
//                         style={{ width: "250px" }}
//                         className="hide-reveal"
//                       />
//                     </div>
//                   </div>

//                   <div className="inputDiv">
//                     <label htmlFor="confirm_password">Confirm Password</label>
//                     <div
//                       className="lg-input lg-flex"
//                       style={inputState ? { border: "1px solid red" } : {}}
//                     >
//                       <RiLockPasswordFill className="iconav" style={{ color: "black" }} />
//                       <input
//                         type={showPassword ? "text" : "password"}
//                         id="confirm_password"
//                         placeholder="Confirm New Password"
//                         value={resetConfirmPassword}
//                         onChange={(e) => setResetConfirmPassword(e.target.value)}
//                         className="hide-reveal"
//                       />
//                       <span
//                         className="eye-icon"
//                         onClick={togglePasswordVisibility}
//                         style={{ cursor: "pointer", marginLeft: "45px" }}
//                       >
//                         {showPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
//                       </span>
//                     </div>
//                   </div>
//                 </>
//               )}

//               {/* Remember Me + Forgot Password */}
//               {!needsPasswordReset && (
//                 <div className="forgotPassword">
//                   <input
//                     type="checkbox"
//                     id="rememberMe"
//                     name="rememberMe"
//                     checked={rememberMe}
//                     onChange={handleRememberMeChange}
//                   />
//                   <label
//                     className="logged-in"
//                     htmlFor="rememberMe"
//                     style={{ color: "rgb(66, 66, 66)", marginLeft: "5px" }}
//                   >
//                     Keep Me Logged In
//                   </label>
//                   <a
//                     onClick={handleForgotPasswordClick}
//                     style={{
//                       marginLeft: "30px",
//                       color: "#4933ff",
//                       fontWeight: "600",
//                       cursor: "pointer",
//                     }}
//                     className="password-forget"
//                   >
//                     Forgot Password?
//                   </a>
//                 </div>
//               )}
//             </div>

//             <button
//               type="submit"
//               className="lg-btn lg-flex"
//               onClick={needsPasswordReset ? handleResetPassword : handleLogin}
//               style={{
//                 marginTop: "50px",
//                 opacity: isSubmitting || isResetting ? 0.7 : 1,
//                 pointerEvents: isSubmitting || isResetting ? "none" : "auto",
//               }}
//               disabled={isSubmitting || isResetting}
//             >
//               {needsPasswordReset
//                 ? isResetting ? "Resetting..." : "Submit"
//                 : isSubmitting ? "Logging in..." : "Login"}
//             </button>
//           </form>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Login;